import { useEffect, useRef, useState, type FormEvent } from "react";
import { demoPlans } from "@shared/demo-request";

export default function DemoForm({ plan, close }: { plan: string | null; close: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const requestId = useRef("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<{ email: string; time: string; timezone: string } | null>(null);
  const [timezone, setTimezone] = useState("America/Los_Angeles");

  useEffect(() => {
    if (!plan) {
      dialog.current?.close();
      return;
    }
    requestId.current = crypto.randomUUID();
    setError("");
    setSuccess(null);
    setBusy(false);
    setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    dialog.current?.showModal();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [plan]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const values = new FormData(event.currentTarget);
    const preferred = new Date(`${values.get("date")}T${values.get("time")}`);
    if (!Number.isFinite(preferred.getTime()) || preferred.getTime() <= Date.now()) {
      setError("Choose a date and time in the future.");
      return;
    }
    setBusy(true);
    setError("");
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15_000);
    try {
      const response = await fetch("/api/demo-requests", {
        method: "POST",
        signal: controller.signal,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: requestId.current,
          name: values.get("name"),
          email: values.get("email"),
          business: values.get("business"),
          plan: values.get("plan"),
          preferredAt: preferred.toISOString(),
          timezone,
          goals: values.get("goals"),
          website: values.get("website"),
        }),
      });
      const result = (await response.json()) as {
        error?: string;
        email?: string;
        preferredAt?: string;
        timezone?: string;
      };
      if (!response.ok) throw new Error(result.error || "Please try again.");
      if (!result.email || !result.preferredAt || !result.timezone) {
        throw new Error("We could not confirm your saved request. Please retry to check it.");
      }
      setSuccess({
        email: result.email,
        time: new Date(result.preferredAt).toLocaleString(undefined, {
          dateStyle: "long",
          timeStyle: "short",
          timeZone: result.timezone,
        }),
        timezone: result.timezone,
      });
    } catch (reason) {
      setError(
        reason instanceof Error && reason.name === "AbortError"
          ? "The request is taking longer than expected. Please retry to check or save it. This will not create a duplicate."
          : reason instanceof Error
            ? reason.message
            : "We could not save your request. Please try again.",
      );
    } finally {
      window.clearTimeout(timeout);
      setBusy(false);
    }
  }

  const today = new Date();
  const minDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const latest = new Date(today.getTime() + 365 * 86_400_000);
  const maxDate = `${latest.getFullYear()}-${String(latest.getMonth() + 1).padStart(2, "0")}-${String(latest.getDate()).padStart(2, "0")}`;

  return (
    <dialog ref={dialog} className="mo-dialog mo-demo-dialog" aria-labelledby="demo-title" onClose={close}
      onCancel={(event) => { if (busy) event.preventDefault(); }}
      onClick={(event) => { if (event.target === event.currentTarget && !busy) close(); }}>
      <button type="button" className="mo-dialog-close" onClick={close} disabled={busy} aria-label="Close demo form">×</button>
      {success ? (
        <div className="demo-success" role="status">
          <span className="demo-success-mark" aria-hidden="true">✓</span>
          <p className="mo-kicker">YOU’RE ON THE LIST</p>
          <h2 id="demo-title">Demo request received.</h2>
          <p>Your saved preferred time is <strong>{success.time}</strong> ({success.timezone}).</p>
          <p>Your request is saved with <strong>{success.email}</strong>. This time is requested, not yet confirmed. No calendar invitation has been sent.</p>
          <button className="mo-preview-button" onClick={close}>Back to PersonaOS <span aria-hidden="true">→</span></button>
        </div>
      ) : (
        <>
          <p className="mo-kicker">MEET YOUR NEXT GREAT HIRE</p>
          <h2 id="demo-title">Let’s meet Mo.</h2>
          <p>Tell us a little about your business and choose a preferred time for your demo.</p>
          <form onSubmit={submit} key={plan} className="demo-form">
            <div className="demo-two-col">
              <label>Your name<input name="name" autoComplete="name" required minLength={2} maxLength={100} placeholder="Alex Morgan" /></label>
              <label>Work email<input name="email" type="email" autoComplete="email" required maxLength={254} placeholder="alex@yourbusiness.com" /></label>
            </div>
            <label>Business name<input name="business" autoComplete="organization" required minLength={2} maxLength={150} placeholder="Your business" /></label>
            <label>I’m interested in<select name="plan" defaultValue={plan ?? "Not sure yet"}>{demoPlans.map((item) => <option key={item}>{item}</option>)}</select></label>
            <div className="demo-two-col">
              <label>Preferred date<input name="date" type="date" min={minDate} max={maxDate} required aria-describedby="demo-timezone" /></label>
              <label>Preferred time<input name="time" type="time" required step="900" aria-describedby="demo-timezone" /></label>
            </div>
            <p className="demo-timezone" id="demo-timezone">Times are in {timezone.replaceAll("_", " ")}.</p>
            <label>What would you like Mo to help with? <span className="demo-optional">Optional</span><textarea name="goals" maxLength={1500} rows={3} placeholder="A new website, more consistent social, growing the business…" /></label>
            <label className="demo-honeypot" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
            {error && <p className="demo-error" role="alert">{error}</p>}
            <button className="mo-preview-button" type="submit" disabled={busy}>{busy ? "Saving your request…" : "Request my demo"}<span aria-hidden="true">→</span></button>
            <p className="mo-dialog-note">We’ll use these details to coordinate your demo. Your preferred time is a request and is subject to confirmation.</p>
          </form>
        </>
      )}
    </dialog>
  );
}